import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { CreateOrderSchema, UpdateOrderSchema } from "@/api.schemas";
import { useSession } from "@/services/auth0/session";
import { Order } from "@/api.types";
import { logger } from "@/services/logger";

interface PaginationMetadata {
  totalCount: number;
  totalPages: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface OrdersResponse {
  data: Order[];
  metadata: PaginationMetadata;
}

export const useCreateOrder = (options: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async (values: CreateOrderSchema) => {
      const { data } = await axios.post<{ orderId: string }>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/create`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customerOrders", customer?.id],
      });
      options.onSuccess?.();
    },
    onError: (error) => {
      logger.error("Error creating order:", error);
      options.onError?.(error);
    },
  });
};

// For fetching orders
export function useCustomerOrders({
  page,
  limit,
}: {
  page: number;
  limit: number;
}) {
  const { token, customer } = useSession();

  return useQuery<OrdersResponse>({
    queryKey: ["customerOrders", customer?.id, page, limit],
    queryFn: async () => {
      const response = await axios.get<OrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/orders`,
        {
          params: {
            page,
            limit,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token && !!customer?.id,
  });
}

// For managing order labels
export function useOrderLabels() {
  const { token } = useSession();

  const printLabels = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await axios.get<Blob>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/shipping-labels`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return response.data;
    },
  });

  const printBulkLabels = useMutation({
    mutationFn: async (orderIds: string[]) => {
      const response = await axios.post<Blob>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/bulk-shipping-labels`,
        { orderIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return response.data;
    },
  });

  return { printLabels, printBulkLabels };
}

// For order status management
export function useOrderStatus() {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customerOrders", customer?.id],
      });
    },
  });
}

// Add single order fetch hook
export function useOrderById(orderId?: string) {
  const { token } = useSession();

  return useQuery<Order>({
    queryKey: ["order", orderId],
    queryFn: async () => {
      const response = await axios.get<Order>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/as-customer`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token && !!orderId,
  });
}

// Add new update order hook
export function useUpdateOrder(
  orderId: string,
  options?: {
    onSuccess?: () => void;
  }
) {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: UpdateOrderSchema) => {
      const response = await axios.patch<Order>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/as-customer`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrders", customer?.id],
      });
      options?.onSuccess?.();
    },
  });
}
